import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getOpportunities,
  getOpportunityById,
  getMyPostings,
  createOpportunity,
  updateOpportunity,
  closeOpportunity,
  deleteOpportunity,
  applyForOpportunity,
  getMyApplications,
  getApplications,
  updateApplicationStatus,
} from '../api/opportunities-api';

const STALE_TIME = 1000 * 60 * 2; // 2 minutes — prevents aggressive refetch on tab focus

// ── Opportunities ─────────────────────────────────────────────────────────────
export const useOpportunities = (filters = {}) => {
  return useQuery({
    queryKey: ['opportunities', filters],
    queryFn: () => getOpportunities(filters),
    staleTime: STALE_TIME,
  });
};

export const useOpportunityById = (id) => {
  return useQuery({
    queryKey: ['opportunity', id],
    queryFn: () => getOpportunityById(id),
    enabled: !!id,
    staleTime: STALE_TIME,
  });
};

export const useMyPostings = () => {
  return useQuery({
    queryKey: ['my-postings'],
    queryFn: getMyPostings,
    staleTime: STALE_TIME,
  });
};

export const useCreateOpportunity = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createOpportunity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunities'] });
      queryClient.invalidateQueries({ queryKey: ['my-postings'] });
    },
  });
};

export const useUpdateOpportunity = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateOpportunity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunities'] });
      queryClient.invalidateQueries({ queryKey: ['my-postings'] });
      // Refresh panels sidebar — editing a job can create a panel
      queryClient.invalidateQueries({ queryKey: ['my-panels'] });
      queryClient.invalidateQueries({ queryKey: ['panel-by-opportunity'] });
    },
  });
};

export const useCloseOpportunity = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: closeOpportunity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-postings'] });
      queryClient.invalidateQueries({ queryKey: ['opportunities'] });
    },
  });
};

export const useDeleteOpportunity = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteOpportunity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-postings'] });
      queryClient.invalidateQueries({ queryKey: ['opportunities'] });
    },
  });
};

// ── Applications ──────────────────────────────────────────────────────────────
export const useApplyForOpportunity = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: applyForOpportunity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunities'] });
      queryClient.invalidateQueries({ queryKey: ['my-applications'] });
      // Auto-join the discussion panel when applying
      queryClient.invalidateQueries({ queryKey: ['my-panels'] });
    },
  });
};

export const useMyApplications = () => {
  return useQuery({
    queryKey: ['my-applications'],
    queryFn: getMyApplications,
    staleTime: STALE_TIME,
  });
};

export const useApplications = (opportunityId) => {
  return useQuery({
    queryKey: ['applications', opportunityId],
    queryFn: () => getApplications(opportunityId),
    enabled: !!opportunityId,
    staleTime: STALE_TIME,
  });
};

export const useUpdateApplicationStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateApplicationStatus,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['applications', variables.opportunityId] });
    },
  });
};
